/**
 * Journey BACKFILL — recovering the route the native background location
 * service captured while the WebView was asleep (phone locked / Maps in
 * front). Pure helpers shared by the client (live/replay split, downsample)
 * and the session-journey lambda (validate + merge), plus the backdated dwell
 * arrival the same buffered fixes make possible.
 *
 * LIVE vs BACKFILL (coordinator decision, PR #440 review):
 *
 *  - A fix whose OWN timestamp is at most {@link JOURNEY_PING_MAX_FIX_AGE_MS}
 *    old at delivery is LIVE: it updates the member-visible position/ETA and
 *    goes out through the normal ping cadence (own timestamp).
 *  - Anything older is a REPLAYED fix: sent as a backfill batch (own
 *    timestamps, downsampled to the live ping cadence, capped at
 *    {@link JOURNEY_BACKFILL_MAX_PER_REQUEST}). The server merges it into the
 *    journey track for travel-leg km / route, stamps each fix with a server
 *    ETA + stage evaluated at its own timestamp, and sends NO push of any
 *    kind (see {@link mergeJourneyBackfill} for the journey-level effects).
 *  - If a backfill fix satisfies `shouldAutoArrive`, arrival is recorded
 *    BACKDATED to that fix's timestamp (not "now").
 *
 * Cadence/freshness/accuracy knobs and the stored-ping bound come from
 * `journey-ping-policy.ts` (PR #441). {@link rebuildJourneyPings} folds
 * through `appendJourneyPing` so compaction + `cumulativeKm` stay correct.
 */

import { AUTO_START_DWELL_MS, classifyDwellFix } from './auto-start'
import { haversineKm, IMPLAUSIBLE_SPEED_KMH } from './km'
import {
  computeJourneyEtaMinutes,
  computeJourneyStage,
  JOURNEY_ARRIVED_STAGE,
  JOURNEY_VERY_CLOSE_STAGE,
  shouldAutoArrive,
} from './journey'
import { etaBracketForMinutes } from './journey-eta-bracket'
import {
  appendJourneyPing,
  JOURNEY_PING_MAX_ACCURACY_METERS,
  JOURNEY_PING_MAX_FIX_AGE_MS,
  JOURNEY_PING_MIN_DISTANCE_METERS,
  JOURNEY_PING_MIN_INTERVAL_MS,
} from './journey-ping-policy'
import type { GeoPoint, GeoStamp, JourneyPing, SessionJourney } from './types'

/** Hard cap on fixes per backfill request (after downsampling). */
export const JOURNEY_BACKFILL_MAX_PER_REQUEST = 60

/** Client-side clock skew tolerated for "not in the future". */
export const BACKFILL_FUTURE_SKEW_MS = 60_000

/** How far before `journey.startedAt` a fix may sit (clock drift between devices/servers). */
export const BACKFILL_BEFORE_START_TOLERANCE_MS = 30_000

/** Wire shape of one backfilled fix (`POST …/journey/ping` body `backfill[]`). */
export interface JourneyBackfillFix {
  lat: number
  lng: number
  /** Epoch ms — the fix's OWN capture time. */
  timestamp: number
  accuracy?: number
  /** Platform mock/fake-location flag — server rejects these (`mock`). */
  isMock?: boolean
}

/** Whether a just-delivered fix counts as live (by its own timestamp). */
export function isLiveJourneyFix(fix: Pick<GeoStamp, 'timestamp'>, now: number = Date.now()): boolean {
  return now - fix.timestamp <= JOURNEY_PING_MAX_FIX_AGE_MS
}

function accurateEnough(fix: Pick<GeoStamp, 'accuracy'>): boolean {
  return (
    fix.accuracy != null &&
    Number.isFinite(fix.accuracy) &&
    fix.accuracy >= 0 &&
    fix.accuracy <= JOURNEY_PING_MAX_ACCURACY_METERS
  )
}

/**
 * Downsample a batch of replayed fixes to the LIVE ping cadence by their own
 * timestamps/positions — keep a fix once at least the cadence interval OR
 * the cadence distance has passed since the last kept one (the same
 * time-or-distance rule the live policy applies), dropping coarse and mock
 * fixes first. The newest (non-mock, accurate) fix is always kept. Output is
 * time-sorted; request-size chunking lives in the client queue (oldest first,
 * 60 per request) so a long lock stretch is not thinned by dropping the
 * oldest points.
 */
export function downsampleBackfillFixes<T extends GeoStamp>(fixes: readonly T[]): T[] {
  const usable = fixes.filter((f) => Number.isFinite(f.timestamp) && !f.isMock && accurateEnough(f))
  if (usable.length === 0) return []
  const sorted = [...usable].sort((a, b) => a.timestamp - b.timestamp)
  const kept: T[] = []
  let last: T | null = null
  for (let i = 0; i < sorted.length; i++) {
    const fix = sorted[i]
    const isLast = i === sorted.length - 1
    const due =
      last == null ||
      fix.timestamp - last.timestamp >= JOURNEY_PING_MIN_INTERVAL_MS ||
      haversineKm(last, fix) * 1000 >= JOURNEY_PING_MIN_DISTANCE_METERS
    if (due || (isLast && last != null && fix.timestamp > last.timestamp)) {
      kept.push(fix)
      last = fix
    }
  }
  return kept
}

/** Convert a GeoStamp into the wire shape (drops speed; carries `isMock`). */
export function toBackfillFix(fix: GeoStamp): JourneyBackfillFix {
  return {
    lat: fix.lat,
    lng: fix.lng,
    timestamp: fix.timestamp,
    ...(fix.accuracy != null && Number.isFinite(fix.accuracy) ? { accuracy: fix.accuracy } : {}),
    ...(fix.isMock ? { isMock: true } : {}),
  }
}

/* ------------------------------ server merge ------------------------------- */

export type BackfillRejectReason =
  | 'malformed'
  | 'mock'
  | 'before-journey-start'
  | 'after-journey-end'
  | 'in-future'
  | 'not-monotonic'
  | 'duplicate'
  | 'implausible-speed'

export interface BackfillValidation {
  /** Time-sorted fixes that passed every gate. */
  accepted: JourneyBackfillFix[]
  rejected: { fix: unknown; reason: BackfillRejectReason }[]
}

function parseBackfillFix(raw: unknown): JourneyBackfillFix | null {
  if (!raw || typeof raw !== 'object') return null
  const r = raw as Record<string, unknown>
  const lat = Number(r.lat)
  const lng = Number(r.lng)
  const timestamp = Number(r.timestamp)
  if (!Number.isFinite(lat) || !Number.isFinite(lng) || !Number.isFinite(timestamp)) return null
  if (Math.abs(lat) > 90 || Math.abs(lng) > 180) return null
  const accuracy = r.accuracy != null ? Number(r.accuracy) : undefined
  return {
    lat,
    lng,
    timestamp,
    ...(accuracy != null && Number.isFinite(accuracy) && accuracy >= 0 ? { accuracy } : {}),
    ...(r.isMock === true ? { isMock: true } : {}),
  }
}

/**
 * Server-side validation of a backfill batch against the journey window:
 *
 *  - well-formed numbers, plausible coordinates;
 *  - not before `journey.startedAt` (minus a small drift tolerance), not
 *    after `journey.arrivedAt` once the journey has *ended* (an `arrived`
 *    journey still accepts the dwell / drive-away tail — chunked 60-fix
 *    flushes arrive the first chunk then POST the rest), not in the
 *    future (plus skew);
 *  - strictly increasing timestamps within the batch (after sorting; ties
 *    and exact duplicates are dropped) and no timestamp already stored on
 *    the journey;
 *  - sane speed: the implied speed from the previous accepted/stored
 *    neighbour must stay under {@link IMPLAUSIBLE_SPEED_KMH} — the same
 *    teleport bound the km filters use.
 *
 * The batch is truncated to `maxPerRequest` before validation.
 */
export function validateJourneyBackfill(input: {
  journey: Pick<SessionJourney, 'status' | 'startedAt' | 'arrivedAt' | 'pings'>
  backfill: unknown
  now?: number
  maxPerRequest?: number
}): BackfillValidation {
  const { journey } = input
  const now = input.now ?? Date.now()
  const max = input.maxPerRequest ?? JOURNEY_BACKFILL_MAX_PER_REQUEST
  const rejected: BackfillValidation['rejected'] = []
  if (!Array.isArray(input.backfill)) return { accepted: [], rejected }

  const parsed: JourneyBackfillFix[] = []
  for (const raw of input.backfill.slice(0, max)) {
    const fix = parseBackfillFix(raw)
    if (!fix) rejected.push({ fix: raw, reason: 'malformed' })
    else parsed.push(fix)
  }
  parsed.sort((a, b) => a.timestamp - b.timestamp)

  const windowStart =
    journey.startedAt != null ? journey.startedAt - BACKFILL_BEFORE_START_TOLERANCE_MS : null
  // `arrived` must keep accepting later GPS (dwell proof / drive-away). A
  // 60-fix oldest-first loop persists `arrived` on the first in-fence chunk;
  // closing the window at `arrivedAt` would drop the rest as after-journey-end.
  // `shouldAutoArrive` is en_route-only, so these later fixes never re-arrive.
  const windowEnd = journey.status === 'ended' ? journey.arrivedAt ?? null : null
  // A clamped live fix is stored at the receive time; its raw device time
  // (`deviceTimestamp`) identifies the same fix in a later backfill.
  const storedTimestamps = new Set(
    journey.pings.flatMap((p) => (p.deviceTimestamp != null ? [p.timestamp, p.deviceTimestamp] : [p.timestamp])),
  )
  const storedGps = journey.pings.filter((p) => p.locationSource !== 'estimate')

  const accepted: JourneyBackfillFix[] = []
  let lastTs = Number.NEGATIVE_INFINITY
  for (const fix of parsed) {
    if (fix.isMock) {
      rejected.push({ fix, reason: 'mock' })
      continue
    }
    if (windowStart != null && fix.timestamp < windowStart) {
      rejected.push({ fix, reason: 'before-journey-start' })
      continue
    }
    if (windowEnd != null && fix.timestamp > windowEnd) {
      rejected.push({ fix, reason: 'after-journey-end' })
      continue
    }
    if (fix.timestamp > now + BACKFILL_FUTURE_SKEW_MS) {
      rejected.push({ fix, reason: 'in-future' })
      continue
    }
    if (storedTimestamps.has(fix.timestamp)) {
      rejected.push({ fix, reason: 'duplicate' })
      continue
    }
    if (fix.timestamp <= lastTs) {
      rejected.push({ fix, reason: 'not-monotonic' })
      continue
    }
    // Speed gate against the nearest earlier neighbour (accepted backfill
    // first, else the latest stored GPS ping before this fix).
    let neighbour: GeoPoint & { timestamp: number } | null =
      accepted.length > 0 ? accepted[accepted.length - 1] : null
    if (!neighbour) {
      for (let i = storedGps.length - 1; i >= 0; i--) {
        if (storedGps[i].timestamp < fix.timestamp) {
          neighbour = storedGps[i]
          break
        }
      }
    }
    if (neighbour) {
      const dtH = (fix.timestamp - neighbour.timestamp) / 3_600_000
      if (dtH > 0 && haversineKm(neighbour, fix) / dtH > IMPLAUSIBLE_SPEED_KMH) {
        rejected.push({ fix, reason: 'implausible-speed' })
        continue
      }
    }
    accepted.push(fix)
    lastTs = fix.timestamp
  }
  return { accepted, rejected }
}

/**
 * Rebuild a journey's stored list from a TIME-SORTED union of stored pings
 * and backfilled fixes, applying the same storage bound the live append
 * path uses — the one place the storage strategy is referenced.
 *
 * Already-stamped pings before the earliest unstamped (new) fix are kept
 * as-is so compaction does not restamp `cumulativeKm`. From that point
 * onward, {@link appendJourneyPing} restamps and re-applies the cap.
 */
export function rebuildJourneyPings(sorted: JourneyPing[]): JourneyPing[] {
  const firstNew = sorted.findIndex((p) => p.cumulativeKm == null)
  if (firstNew < 0) return sorted
  return sorted.slice(firstNew).reduce((acc, p) => appendJourneyPing(acc, p), sorted.slice(0, firstNew))
}

export interface BackfillMergeResult {
  journey: SessionJourney
  /** Set when a backfill fix auto-arrived the journey (backdated to that fix). */
  arrivedAt: number | null
}

/**
 * Server ETA + stage for one fix AS OF ITS OWN TIMESTAMP: the journey is
 * evaluated with only the pings at/before the fix (stored + earlier backfill)
 * and the clock set to the fix time, so a replayed fix gets the same figures
 * a live ping at that moment would have got — no staleness decay for the
 * minutes it sat in the native buffer.
 */
function enrichBackfillPing(input: {
  journey: SessionJourney
  priorPings: readonly JourneyPing[]
  ping: JourneyPing
  destination: GeoPoint | null | undefined
  travelDurationMs: number | null | undefined
}): JourneyPing {
  const { journey, priorPings, ping, destination, travelDurationMs } = input
  const prefix: SessionJourney = { ...journey, status: 'en_route', pings: [...priorPings, ping] }
  let floorStage: number | null = null
  for (const prior of priorPings) {
    if (prior.stage != null && (floorStage == null || prior.stage > floorStage)) floorStage = prior.stage
  }
  const stage = computeJourneyStage({
    journey: { ...prefix, stage: floorStage },
    destination: destination ?? null,
    currentPosition: ping,
    travelDurationMs,
    now: ping.timestamp,
  })
  const etaMinutes = computeJourneyEtaMinutes({
    journey: prefix,
    destination: destination ?? null,
    travelDurationMs,
    now: ping.timestamp,
  })
  return { ...ping, stage, ...(etaMinutes != null ? { etaMinutes } : {}) }
}

/**
 * Merge accepted backfill fixes into the journey: time-sorted union with the
 * stored pings, rebuilt through {@link rebuildJourneyPings}.
 *
 * Every accepted fix is stamped with a SERVER-computed `etaMinutes` and
 * `stage` evaluated at its own timestamp (2026-10-13 field test: 69 of 75
 * stored pings were replayed backfill with neither, so the track carried no
 * ETA from 14:37 until arrival). Fixes at/after arrival carry stage 6 and no
 * ETA.
 *
 * Journey-level state: when the newest accepted fix is newer than every
 * stored ping (the WebView slept through the whole stretch, so the batch IS
 * the freshest evidence), `stage` advances to that fix's stage (monotonic,
 * never past "very close by" — stage 6 is arrival only) and `etaBracket`
 * reflects its ETA. Backfill never sets `nearNotifiedAt`: the "less than
 * 5 min" push is live-only, so the next live ping still fires it.
 *
 * Arrival: the first backfill fix (in time order) that satisfies
 * `shouldAutoArrive` flips the journey to `arrived` with `arrivedAt` = THAT
 * fix's timestamp. Later fixes in the batch stay on the track.
 */
export function mergeJourneyBackfill(input: {
  journey: SessionJourney
  accepted: readonly JourneyBackfillFix[]
  destination: GeoPoint | null | undefined
  /** Pre-shift drive estimate — time-countdown fallback when GPS can't anchor the ETA. */
  travelDurationMs?: number | null
}): BackfillMergeResult {
  const { journey, accepted, destination, travelDurationMs } = input
  if (accepted.length === 0) return { journey, arrivedAt: null }

  const stored = [...journey.pings].sort((a, b) => a.timestamp - b.timestamp)
  const enRoute = journey.status === 'en_route'
  let arrivedAt: number | null = null
  const added: JourneyPing[] = []
  for (const fix of accepted) {
    const raw: JourneyPing = { ...fix, locationSource: 'gps', source: 'backfill' }
    if (arrivedAt == null && enRoute && shouldAutoArrive({ journey, destination, ping: raw })) {
      arrivedAt = fix.timestamp
    }
    const arrivedByNow =
      arrivedAt != null || (journey.arrivedAt != null && fix.timestamp >= journey.arrivedAt)
    if (arrivedByNow || journey.status === 'ended') {
      added.push({ ...raw, stage: JOURNEY_ARRIVED_STAGE })
      continue
    }
    const priorPings = [...stored.filter((p) => p.timestamp < fix.timestamp), ...added].sort(
      (a, b) => a.timestamp - b.timestamp,
    )
    added.push(enrichBackfillPing({ journey, priorPings, ping: raw, destination, travelDurationMs }))
  }

  const sorted = [...journey.pings, ...added].sort((a, b) => a.timestamp - b.timestamp)
  const pings = rebuildJourneyPings(sorted)
  if (arrivedAt != null) {
    return {
      journey: { ...journey, pings, status: 'arrived', arrivedAt, stage: JOURNEY_ARRIVED_STAGE, etaBracket: 'arrived' },
      arrivedAt,
    }
  }

  const newestStoredTs = stored.length > 0 ? stored[stored.length - 1].timestamp : Number.NEGATIVE_INFINITY
  const newestAdded = added[added.length - 1]
  if (enRoute && newestAdded && newestAdded.timestamp > newestStoredTs) {
    const addedStage = Math.min(newestAdded.stage ?? 2, JOURNEY_VERY_CLOSE_STAGE)
    const stage = Math.max(journey.stage ?? 2, addedStage)
    const etaBracket =
      newestAdded.etaMinutes != null ? etaBracketForMinutes(newestAdded.etaMinutes) : journey.etaBracket ?? null
    return { journey: { ...journey, pings, stage, etaBracket }, arrivedAt: null }
  }
  return { journey: { ...journey, pings }, arrivedAt: null }
}

/* ------------------- backdated dwell arrival (phone locked) ---------------- */

/** Tolerance on "the dwell must have elapsed" (client/server clock drift). */
export const AUTO_START_DWELL_TOLERANCE_MS = 30_000

export interface BackgroundDwellCompletion {
  /** Epoch ms the dwell completed — the moment to record as the arrival time. */
  completedAt: number
  /** The last accuracy-gated fix inside the fence — sent as the event's location evidence. */
  evidence: GeoStamp
}

/**
 * Did buffered fixes prove the 3-minute dwell completed while the app was
 * away? Mirrors the foreground dwell contract (shared/sessions/auto-start.ts
 * rules 2–3) over a replayed sequence:
 *
 *  - anchor starts at `arrivedAt`; every accuracy-gated fix OUTSIDE the
 *    arrival fence re-anchors to that fix's time; coarse fixes are ignored;
 *  - the dwell completes at `anchor + AUTO_START_DWELL_MS` — but ONLY if at
 *    least one qualifying INSIDE fix exists at/after the anchor (evidence
 *    the device stayed), the LAST qualifying fix is inside, and the
 *    completion moment is ≤ `now`. No evidence → `null` → the manual path
 *    (the foreground "no retroactive fire" rule still applies).
 */
export function resolveBackgroundDwellCompletion(input: {
  journey: Pick<SessionJourney, 'status' | 'arrivedAt'> | null | undefined
  fixes: readonly GeoStamp[]
  destination: GeoPoint | null | undefined
  now?: number
}): BackgroundDwellCompletion | null {
  const now = input.now ?? Date.now()
  const walked = walkBackgroundDwell(input)
  if (!walked || walked.lastVerdict !== 'inside' || !walked.insideSinceAnchor) return null
  const completedAt = walked.anchor + AUTO_START_DWELL_MS
  if (completedAt > now) return null
  return { completedAt, evidence: walked.insideSinceAnchor }
}

/**
 * Next auto-start deadline from the same replayed walk: `anchor + dwell`
 * when that moment is still in the future (an outside fix re-anchors).
 * `null` when the grace already elapsed without proof — manual path.
 */
export function resolveBackgroundDwellDeadline(input: {
  journey: Pick<SessionJourney, 'status' | 'arrivedAt'> | null | undefined
  fixes: readonly GeoStamp[]
  destination: GeoPoint | null | undefined
  now?: number
}): number | null {
  const walked = walkBackgroundDwell(input)
  if (!walked) return null
  const now = input.now ?? Date.now()
  const deadline = walked.anchor + AUTO_START_DWELL_MS
  return deadline > now ? deadline : null
}

function walkBackgroundDwell(input: {
  journey: Pick<SessionJourney, 'status' | 'arrivedAt'> | null | undefined
  fixes: readonly GeoStamp[]
  destination: GeoPoint | null | undefined
}): {
  anchor: number
  insideSinceAnchor: GeoStamp | null
  lastVerdict: 'inside' | 'outside' | null
} | null {
  const { journey, destination } = input
  if (journey?.status !== 'arrived' || journey.arrivedAt == null || !destination) return null
  const arrivedAt = journey.arrivedAt
  const sorted = input.fixes
    .filter((f) => Number.isFinite(f.timestamp) && f.timestamp >= arrivedAt && !f.isMock)
    .sort((a, b) => a.timestamp - b.timestamp)

  let anchor = arrivedAt
  let insideSinceAnchor: GeoStamp | null = null
  let lastVerdict: 'inside' | 'outside' | null = null
  for (const fix of sorted) {
    const verdict = classifyDwellFix(fix, destination)
    if (verdict === 'ignored') continue
    lastVerdict = verdict
    if (verdict === 'outside') {
      anchor = fix.timestamp
      insideSinceAnchor = null
    } else {
      insideSinceAnchor = fix
    }
  }
  return { anchor, insideSinceAnchor, lastVerdict }
}

export type AutoStartAtValidation =
  | { ok: true; actualStart: number }
  | {
      ok: false
      reason: 'not-finite' | 'no-arrival' | 'before-dwell' | 'in-future' | 'evidence-outside-fence'
    }

/**
 * Server-side gate for a BACKDATED auto-GPS start (`startedAt` on a check-in
 * with `startedBy: 'auto-gps'`), reusing the `validateDepartedAt` pattern:
 *
 *  - must be finite; the journey must have an `arrivedAt`;
 *  - not before `arrivedAt + AUTO_START_DWELL_MS` (minus drift tolerance) —
 *    the dwell cannot complete sooner than the dwell;
 *  - not in the future (plus skew);
 *  - when the session has coordinates and the event carries a location, that
 *    evidence fix must classify INSIDE the arrival fence (accuracy-gated).
 *
 * Rejection never fails the record — the caller falls back to the fix time.
 */
export function validateAutoStartAt(input: {
  startedAt: unknown
  arrivedAt: number | null | undefined
  now: number
  location: (GeoPoint & { accuracy?: number }) | null | undefined
  sessionGeo: GeoPoint | null | undefined
}): AutoStartAtValidation {
  const { startedAt, arrivedAt, now, location, sessionGeo } = input
  if (typeof startedAt !== 'number' || !Number.isFinite(startedAt)) {
    return { ok: false, reason: 'not-finite' }
  }
  if (arrivedAt == null) return { ok: false, reason: 'no-arrival' }
  if (startedAt < arrivedAt + AUTO_START_DWELL_MS - AUTO_START_DWELL_TOLERANCE_MS) {
    return { ok: false, reason: 'before-dwell' }
  }
  if (startedAt > now + BACKFILL_FUTURE_SKEW_MS) return { ok: false, reason: 'in-future' }
  if (sessionGeo && location && classifyDwellFix(location, sessionGeo) !== 'inside') {
    return { ok: false, reason: 'evidence-outside-fence' }
  }
  return { ok: true, actualStart: startedAt }
}
